import { useLandingDiscoveryFeatures } from 'app/hooks/use-landing-discovery-features';
import { PopularCategories } from './components/popular-categories';
import { FestivalReminder } from 'app/components/festival/festival-reminder';
import { useIsFocused } from '@react-navigation/native';
import { PostsList } from 'app/components/posts-list';
import { useHideStepsToComplete } from 'app/components/steps-to-complete';
import { useTutorialRef } from 'app/components/tutorial-overlay';
import { Column } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';
import { useSocialFeedFeature } from 'app/hooks/use-social-feed-feature';
import { usePosts } from 'app/network/post';
import { bottomNavigationBarVisibleAtom, currentTutorialAtom } from 'app/atoms';
import { AmbassadorUsersBanner } from 'app/components/ambassador-users';
import { FavouritesEmptyState } from 'app/components/favourites-empty-state';
import { MultipleOfferCard } from 'app/components/multiple-offer-card';
import { UnlockFullExperienceCard } from './components/unlock-full-experience-card';
import { useOnePreference, useUpsertOnePreference } from 'app/network/preferences';
import { QuickOfferBannerType } from 'app/types/preferences';
import { InsuredUser, useHideInsuredUser } from 'app/components/insured-users';
import { ListingOfferCardSmall } from 'app/components/listing-offer-card.small';
import { ListingRequestCard } from 'app/components/listing-request-card';
import { PostNeedPromptCard } from 'app/components/post-need-prompt-card';
import { PostOfferPromptCard } from 'app/components/post-offer-prompt-card';
import { RecentLoanCard } from 'app/components/recent-loan-card';
import { RecentExchangeCard } from 'app/components/recent-exchange-card';
import { RequestEmptyState } from 'app/components/request-empty-state';
import { SharingPlaceCard } from 'app/components/sharing-place-card';
import { SearchInput } from 'app/design/primitives/input/search';
import { useNavigationOptions } from 'app/hooks/navigation';
import { useAmbassador } from 'app/hooks/use-ambassador';
import { useAllTags, type ClosestListingsByTag } from 'app/network/listing';
import type { RecentLoanListing } from 'app/network/loan';
import { type SharingPlace } from 'app/network/sharing-place';
import { useNeighbourhoodStatus, type NeighbourhoodStatus } from 'app/network/statistics';
import { BonificationPrompt } from 'app/screens/billboard/all-listings/components/bonification-banner';
import { ExpiredLoans } from 'app/screens/billboard/all-listings/components/expired-loans';
import { InviteOthersPrompt } from 'app/screens/billboard/all-listings/components/invite-others-prompt';
import {
  ListingListWithStrategy,
  useFavouritesRecommendationsWithRuntime,
  useMatchingRequestsRecommendationsWithRuntime,
  useMixedRecommendationsWithRuntime,
  useMostPopularRecommendationsWithRuntime,
  useNearestSharingPlaceRecommendationsWithRuntime,
  useRecentOffersRecommendationsWithRuntime,
  useRecentLoansRecommendationsWithRuntime,
  useRecommendationRuntime,
  useUrgentNeedsRecommendationsWithRuntime,
  useYourInterestRecommendationsWithRuntime,
  type RecommendationQueryState,
} from 'app/screens/billboard/all-listings/components/recommended-listings';
import {
  Strategies,
  shouldShowRecommendationSection,
} from 'app/screens/billboard/all-listings/components/recommended-listings.utils';
import { createTheme as createDSTheme } from 'app/shopify';
import { ListingType } from 'app/types/listing';
import type { Loan } from 'app/types/loan';
import type { NavigationScreen } from 'app/types/navigation';
import { FeatureType } from 'app/types/recommender';
import { Tutorial, TutorialRef } from 'app/types/tutorial';
import { WhiteLabel } from 'app/types/white-labels';
import { isAnyWhiteLabel, isWhiteLabel } from 'app/utils/helpers';
import { useRegisterLoading } from 'app/utils/performance/screen-perf-context';
import { isMobileNative } from 'app/utils/web-helpers';
import { useAtomValue, useSetAtom } from 'jotai';
import type { TFunction } from 'i18next';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, View, type ListRenderItem, type NativeScrollEvent, type NativeSyntheticEvent } from 'react-native';
import { FlatList } from 'react-native-gesture-handler';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Animated, {
  FadeOut,
  LinearTransition,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { LandingFeedPager } from './components/landing-feed-tabs';
import { CountBadge } from './components/count-badge';
import { SubscriptionCodeModalPrompt } from './components/subscription-code-modal-prompt';
import {
  DefricheurCard,
  ReferralInviteCard,
  SocialMediaCard,
  WriteToMayorCard,
} from './components/immature-neighbourhood';
import { LandingCategories } from './components/landing-categories';
import { LandingCategoryOffers, useLandingCategoryOffers } from './components/landing-category-offers';
import { PopularInterests, usePopularInterestsTags } from './components/popular-interests';
import { SearchOverlay } from './components/search-overlay';
import { TrialBanner } from './components/trial-banner';
import { UpcomingActivity } from './components/upcoming-activity';
import { buildUpcomingActivityEvents } from './components/upcoming-activity-utils';
import {
  getOrderedGeneralInterestTagIds,
  getSeededUserInterestTagIds,
  getUserInterestTagIds,
} from './landing-interest-tags';
import {
  LandingProfileSummary,
  ProfileCompletionSteps,
  useHasPartagePlusSubscription,
  LandingQuickActions,
  useLandingPageNavigationActions,
  useProfileCompletionSteps,
} from './shared';
import { useLandingPage } from './use-landing-page';
import { useLandingProgressiveLoad } from './use-landing-progressive-load';
import { LandingPageSectionSeparator } from './landing-page-section-separator';
import { LandingInterestRecommendationsBlock } from './landing-interest-recommendations-block.small';

const theme = createDSTheme();
const HOME_LIST_CONTENT_CONTAINER_STYLE = {
  paddingHorizontal: theme.spacing['spacing.general.x4'],
  paddingBottom: theme.spacing['spacing.general.x12'],
  paddingTop: theme.spacing['spacing.general.x3'],
};

interface LandingPageSection {
  key: string;
  node: React.ReactElement;
}

type LandingTab = 'home' | 'feed' | 'catalog';

const SEARCH_BAR_CONTAINER_HEIGHT = 52;

const SEARCH_BAR_SCROLL_DELTA = 52;

function getLandingTabs({ canUseSocialFeed, t }: { canUseSocialFeed: boolean; t: TFunction<'landing-page'> }) {
  const tabs: { label: string; value: LandingTab }[] = [
    {
      label: t('tabs.explore'),
      value: 'home' as const,
    },
  ];
  if (canUseSocialFeed) {
    tabs.push({
      label: t('tabs.feed'),
      value: 'feed' as const,
    });
  }
  return tabs;
}

function useLandingTabs() {
  const { t } = useTranslation('landing-page');
  const { canUseSocialFeed } = useSocialFeedFeature();
  const [requestedTab, setTab] = useState<LandingTab>('home');
  const setRequestedTab = (tab: LandingTab) => {
    setTab(tab);
  };
  const activeTab = canUseSocialFeed ? requestedTab : 'home';
  const postsQuery = usePosts({ enabled: canUseSocialFeed });
  const { data: postsData, fetchNextPage, hasNextPage, isFetchingNextPage, isPending: arePostsPending } = postsQuery;
  const recentPosts = postsData?.pages.flatMap((page) => page.results) ?? [];
  const loadMorePosts = () => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  };

  return {
    activeTab,
    arePostsPending,
    isEndOfFeed: postsQuery.isSuccess && !hasNextPage,
    isFetchingNextPage,
    landingTabs: getLandingTabs({ canUseSocialFeed, t }),
    loadMorePosts,
    recentPosts,
    setRequestedTab,
  };
}

function selectLandingTab({
  handleViewAllListings,
  setRequestedTab,
  tab,
}: {
  handleViewAllListings: () => void;
  setRequestedTab: (tab: LandingTab) => void;
  tab: LandingTab;
}) {
  if (tab === 'catalog') {
    handleViewAllListings();
    return;
  }
  setRequestedTab(tab);
}

function hasVisibleUpcomingActivity({
  borrowings,
  lendings,
  visibleExpiredLoans,
}: {
  borrowings: Loan[] | undefined;
  lendings: Loan[] | undefined;
  visibleExpiredLoans: Loan[];
}) {
  const excludedLoanIds = new Set(visibleExpiredLoans.map((loan) => loan.id));
  return buildUpcomingActivityEvents({ borrowings: borrowings ?? [], lendings: lendings ?? [] }).some(
    (event) => !excludedLoanIds.has(event.loan.id),
  );
}

function isDeferredTagRecommendationEnabled(isDeferred: boolean, tagCount: number) {
  return isDeferred && tagCount > 0;
}

function shouldShowPopularInterests(enabled: boolean, tags: unknown[] | undefined) {
  return enabled && (tags === undefined || tags.length > 0);
}

function getImmatureNeighbourhood(isPending: boolean, status: NeighbourhoodStatus | null | undefined) {
  if (isPending || status?.isMatureNeighbourhood !== false) return undefined;
  return status;
}

function updateSearchBarFromScroll({
  event,
  isSearchBarVisibleRef,
  lastScrollYRef,
  scrollDirectionAccumulationRef,
  setSearchBarVisible,
}: {
  event: NativeSyntheticEvent<NativeScrollEvent>;
  isSearchBarVisibleRef: React.RefObject<boolean>;
  lastScrollYRef: React.RefObject<number>;
  scrollDirectionAccumulationRef: React.RefObject<number>;
  setSearchBarVisible: (visible: boolean) => void;
}) {
  const offsetY = Math.max(0, event.nativeEvent.contentOffset.y);
  const deltaY = offsetY - lastScrollYRef.current;

  if (offsetY <= theme.spacing['spacing.general.x2']) {
    scrollDirectionAccumulationRef.current = 0;
    setSearchBarVisible(true);
    lastScrollYRef.current = offsetY;
    return;
  }
  if (deltaY > 0) {
    scrollDirectionAccumulationRef.current =
      scrollDirectionAccumulationRef.current > 0 ? scrollDirectionAccumulationRef.current + deltaY : deltaY;
    if (isSearchBarVisibleRef.current && scrollDirectionAccumulationRef.current > SEARCH_BAR_SCROLL_DELTA) {
      setSearchBarVisible(false);
      scrollDirectionAccumulationRef.current = 0;
    }
  } else if (deltaY < 0) {
    scrollDirectionAccumulationRef.current =
      scrollDirectionAccumulationRef.current < 0 ? scrollDirectionAccumulationRef.current + deltaY : deltaY;
    if (!isSearchBarVisibleRef.current && scrollDirectionAccumulationRef.current < -SEARCH_BAR_SCROLL_DELTA) {
      setSearchBarVisible(true);
      scrollDirectionAccumulationRef.current = 0;
    }
  }
  lastScrollYRef.current = offsetY;
}

function useLandingSearchBar({
  activeTab,
  isFocused,
  isHomeExploreTutorialActive,
}: {
  activeTab: LandingTab;
  isFocused: boolean;
  isHomeExploreTutorialActive: boolean;
}) {
  const searchBarVisibility = useSharedValue(1);
  const [isSearchBarVisible, setIsSearchBarVisible] = useState(true);
  const setBottomNavigationBarVisible = useSetAtom(bottomNavigationBarVisibleAtom);
  const listRef = useRef<FlatList<LandingPageSection>>(null);
  const isSearchBarVisibleRef = useRef(true);
  const lastScrollYRef = useRef(0);
  const savedScrollOffsets = useRef<Partial<Record<LandingTab, number>>>({});
  const scrollDirectionAccumulationRef = useRef(0);
  const { viewRef: headerSearchBarRef } = useTutorialRef(TutorialRef.HeaderSearchBar);
  const searchBarAnimatedStyle = useAnimatedStyle(
    () => ({
      height: SEARCH_BAR_CONTAINER_HEIGHT,
      opacity: searchBarVisibility.value,
      transform: [{ translateY: -12 * (1 - searchBarVisibility.value) }],
    }),
    [],
  );
  const setSearchBarVisible = (nextVisible: boolean) => {
    if (isSearchBarVisibleRef.current === nextVisible) return;
    isSearchBarVisibleRef.current = nextVisible;
    setIsSearchBarVisible(nextVisible);
    searchBarVisibility.set(withTiming(nextVisible ? 1 : 0, { duration: 180 }));
    if (isMobileNative) setBottomNavigationBarVisible(nextVisible);
  };
  const showSearchBarFromEffect = React.useEffectEvent(() => setSearchBarVisible(true));

  React.useEffect(() => {
    lastScrollYRef.current = savedScrollOffsets.current[activeTab] ?? 0;
    scrollDirectionAccumulationRef.current = 0;
    showSearchBarFromEffect();
  }, [activeTab]);

  React.useEffect(() => {
    if (!isFocused) showSearchBarFromEffect();
    return () => setBottomNavigationBarVisible(true);
  }, [isFocused, setBottomNavigationBarVisible]);
  React.useEffect(() => {
    if (!isFocused || !isHomeExploreTutorialActive) return;
    lastScrollYRef.current = 0;
    scrollDirectionAccumulationRef.current = 0;
    showSearchBarFromEffect();
    setBottomNavigationBarVisible(true);
    listRef.current?.scrollToOffset({ animated: false, offset: 0 });
  }, [isFocused, isHomeExploreTutorialActive, setBottomNavigationBarVisible]);

  const handleScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    savedScrollOffsets.current[activeTab] = event.nativeEvent.contentOffset.y;
    updateSearchBarFromScroll({
      event,
      isSearchBarVisibleRef,
      lastScrollYRef,
      scrollDirectionAccumulationRef,
      setSearchBarVisible,
    });
  };

  return { handleScroll, headerSearchBarRef, isSearchBarVisible, listRef, searchBarAnimatedStyle };
}

type LandingPageScreenProps = React.ComponentProps<NavigationScreen<'landing-page'>>;

function useLandingQuickOfferBanner(
  isLoggedIn: boolean,
  completeProfileSteps: ReturnType<typeof useProfileCompletionSteps>,
) {
  const addListingCompleted = completeProfileSteps.steps.find((step) => step.id === 'add-listing')?.completed ?? false;
  const { data: quickOfferPreferenceData, isSuccess: isQuickOfferPreferenceSuccess } =
    useOnePreference('quickOfferBanner');
  const quickOfferPreference = quickOfferPreferenceData?.value ?? QuickOfferBannerType.nonHideable;
  const showTopMultipleOfferCard =
    isLoggedIn &&
    (!addListingCompleted || (isQuickOfferPreferenceSuccess && quickOfferPreference !== QuickOfferBannerType.hidden));
  const { mutate: setQuickOfferPreference } = useUpsertOnePreference('quickOfferBanner');
  const dismissTopMultipleOfferCard = () => setQuickOfferPreference(QuickOfferBannerType.hidden);
  return { showTopMultipleOfferCard, dismissTopMultipleOfferCard };
}

const useLandingPageScreenModel = ({ navigation }: LandingPageScreenProps) => {
  const { showDiscoveryFeeds, showLandingCategories } = useLandingDiscoveryFeatures();
  const actions = useLandingPageNavigationActions(navigation);
  const hasPartagePlusSubscription = useHasPartagePlusSubscription();
  const { t } = useTranslation('recommendations');
  const tabs = useLandingTabs();
  const { activeTab } = tabs;

  const {
    profile,
    user,
    stats,
    userInterests,
    interestsAvailable,
    unreadData,
    isLoggedIn,
    freeSubAvailable,
    freeSubNumDaysLeft,
    hasCustomProfilePicture,
    showInviteOthersPrompt,
    unreadCount,
    activitySubtitle,
    greeting,
    addressLabel,
    isSearchOverlayVisible,
    setIsSearchOverlayVisible,
    completeProfilePromptCompleted,
    setCompleteProfilePromptCompleted,
    handleSearchSubmit,
    handleTagPress,
    handleCategoryPress,
    handleViewAllListings,
    canPublish,
    canViewActivitySummary,
    lendings,
    borrowings,
    loansQuerySettled,
    visibleExpiredLoans,
    hideExpiredLoan,
    bonificationBanner,
  } = useLandingPage();
  const handleLandingTabPress = (tab: LandingTab) => {
    selectLandingTab({ handleViewAllListings, setRequestedTab: tabs.setRequestedTab, tab });
  };
  const {
    dismissable: bonificationDismissable,
    dismissed: bonificationDismissed,
    numberOfListingsToBonify,
    setClosedBonificationBanner,
    setDismissed: setBonificationDismissed,
    shouldHideBonificationBanner,
  } = bonificationBanner;
  const stableBonificationBanner = {
    dismissable: bonificationDismissable,
    dismissed: bonificationDismissed,
    numberOfListingsToBonify,
    setClosedBonificationBanner,
    setDismissed: setBonificationDismissed,
    shouldHideBonificationBanner,
  };
  const hasUpcomingActivity = hasVisibleUpcomingActivity({ borrowings, lendings, visibleExpiredLoans });
  useNavigationOptions(navigation, { headerShown: !isSearchOverlayVisible });

  const completeProfileSteps = useProfileCompletionSteps({
    actions,
    hasCustomProfilePicture,
    profile,
    setCompleteProfilePromptCompleted,
    user,
    userInterests,
  });
  const hideStepsToComplete = useHideStepsToComplete();
  const { showTopMultipleOfferCard, dismissTopMultipleOfferCard } = useLandingQuickOfferBanner(
    isLoggedIn,
    completeProfileSteps,
  );
  const { isDeferredQueryEnabled, needsTags } = useLandingProgressiveLoad({ enabled: activeTab === 'home' });
  const { data: tags, isPending: isTagsPending } = useAllTags(false, { enabled: interestsAvailable && needsTags });
  useRegisterLoading(isTagsPending && interestsAvailable && needsTags, 'LandingPageScreen-tags');
  const interestSelectionSeed = React.useId();
  const userInterestTagIds = getUserInterestTagIds(userInterests);
  const randomUserInterestTagIds = getSeededUserInterestTagIds(userInterestTagIds, 3, interestSelectionSeed);
  const visibleGeneralInterestTagIds = getOrderedGeneralInterestTagIds(userInterests, tags).slice(0, 3);
  const canBecomeAmbassador = useAmbassador().canBecomeAmbassador;
  const recommendationRuntime = useRecommendationRuntime();
  const urgentNeedsRecommendations = useUrgentNeedsRecommendationsWithRuntime(
    { strategy: Strategies.UrgentNeeds },
    recommendationRuntime,
  );
  const urgentNeedsCount = urgentNeedsRecommendations.count;
  useRegisterLoading(urgentNeedsRecommendations.isPending, 'LandingPageScreen-urgent-needs');
  const recentOffersRecommendations = useRecentOffersRecommendationsWithRuntime(
    { strategy: Strategies.RecentOffers },
    recommendationRuntime,
  );
  useRegisterLoading(recentOffersRecommendations.isPending, 'LandingPageScreen-recent-offers');
  const recentLoansRecommendations = useRecentLoansRecommendationsWithRuntime(
    { strategy: Strategies.RecentLoans },
    recommendationRuntime,
  );
  const mixedRecommendationsEnabled = isDeferredQueryEnabled('mixed-recommendations');
  const mixedRecommendations = useMixedRecommendationsWithRuntime(
    {
      strategy: Strategies.UseMixedRecommendations,
      recommendationType: ListingType.offer,
      similarityType: FeatureType.CONTENT,
      enabled: mixedRecommendationsEnabled,
    },
    recommendationRuntime,
  );
  const nearestSharingPlaceEnabled = isDeferredQueryEnabled('nearest-sharing-place');
  const nearestSharingPlaceRecommendations = useNearestSharingPlaceRecommendationsWithRuntime(
    {
      strategy: Strategies.NearestSharingPlace,
      enabled: nearestSharingPlaceEnabled,
    },
    recommendationRuntime,
  );
  const matchingRequestsEnabled = isDeferredQueryEnabled('matching-requests');
  const matchingRequestsRecommendations = useMatchingRequestsRecommendationsWithRuntime(
    {
      strategy: Strategies.UseMatchNeedsWithOffers,
      recommendationType: ListingType.request,
      similarityType: FeatureType.CONTENT,
      enabled: matchingRequestsEnabled,
    },
    recommendationRuntime,
  );
  const randomInterestEnabled = isDeferredTagRecommendationEnabled(
    interestsAvailable && isDeferredQueryEnabled('your-interest-random'),
    randomUserInterestTagIds.length,
  );
  const randomInterestRecommendations = useYourInterestRecommendationsWithRuntime(
    {
      strategy: Strategies.YourInterest,
      tagIds: randomUserInterestTagIds,
      enabled: randomInterestEnabled,
    },
    recommendationRuntime,
  );
  const mostPopularEnabled = isDeferredQueryEnabled('most-popular');
  const mostPopularRecommendations = useMostPopularRecommendationsWithRuntime(
    {
      strategy: Strategies.MostPopular,
      recommendationType: ListingType.offer,
      similarityType: FeatureType.CONTENT,
      enabled: mostPopularEnabled,
    },
    recommendationRuntime,
  );
  const favouritesEnabled = isDeferredQueryEnabled('favourites');
  const favouritesRecommendations = useFavouritesRecommendationsWithRuntime(
    {
      strategy: Strategies.YourFavourites,
      enabled: favouritesEnabled,
    },
    recommendationRuntime,
  );
  const generalInterestEnabled = isDeferredTagRecommendationEnabled(
    interestsAvailable && isDeferredQueryEnabled('your-interest-general'),
    visibleGeneralInterestTagIds.length,
  );
  const generalInterestRecommendations = useYourInterestRecommendationsWithRuntime(
    {
      strategy: Strategies.YourInterest,
      tagIds: visibleGeneralInterestTagIds,
      enabled: generalInterestEnabled,
    },
    recommendationRuntime,
  );
  const popularInterestsEnabled =
    interestsAvailable && !showLandingCategories && isDeferredQueryEnabled('popular-interests');
  const popularInterestsQuery = usePopularInterestsTags({ enabled: popularInterestsEnabled });
  useRegisterLoading(
    randomInterestRecommendations.isPending || generalInterestRecommendations.isPending,
    'LandingPageScreen-your-interest',
  );
  const currentTutorial = useAtomValue(currentTutorialAtom);
  const isHomeExploreTutorialActive = currentTutorial === Tutorial.HomeExplore;
  const { data: neighbourhoodStatus, isPending: isNeighbourhoodStatusPending } = useNeighbourhoodStatus({
    enabled: isLoggedIn,
  });
  const immatureNeighbourhood = getImmatureNeighbourhood(isNeighbourhoodStatusPending, neighbourhoodStatus);

  const renderSection: ListRenderItem<LandingPageSection> = ({ item }) => item.node;
  const keyExtractor = (section: LandingPageSection) => section.key;

  const { hideInsuredUser } = useHideInsuredUser({ otherUser: user, placement: 'landing-page' });
  const categoryOffers = useLandingCategoryOffers(activeTab === 'home');
  const showRecommendationSection = (
    section: Pick<RecommendationQueryState, 'featureEnabled' | 'isPending' | 'show'>,
    options?: { hasEmptyState?: boolean; ignoresFeatureFlag?: boolean },
  ) => shouldShowRecommendationSection(section, options);
  const visibleRandomInterestRecommendations = interestsAvailable
    ? (randomInterestRecommendations.recommendations as ClosestListingsByTag[] | undefined)?.slice(
        0,
        randomUserInterestTagIds.length,
      )
    : undefined;
  const visibleGeneralInterestRecommendations = interestsAvailable
    ? (generalInterestRecommendations.recommendations as ClosestListingsByTag[] | undefined)?.slice(
        0,
        visibleGeneralInterestTagIds.length,
      )
    : undefined;
  const showPopularInterests = shouldShowPopularInterests(popularInterestsEnabled, popularInterestsQuery.data);

  const sections: LandingPageSection[] = (() => {
    const items: LandingPageSection[] = [
      ...(isWhiteLabel(WhiteLabel.Refrain) ? [{ key: 'festival-reminder', node: <FestivalReminder /> }] : []),
      ...(visibleExpiredLoans.length > 0 || !shouldHideBonificationBanner
        ? [
            {
              key: 'alerts-strip',
              node: (
                <Column
                  style={{
                    columnGap: theme.spacing['spacing.general.x4'],
                    rowGap: theme.spacing['spacing.general.x4'],
                    paddingRight: theme.spacing['spacing.general.x1'],
                  }}
                >
                  {visibleExpiredLoans.length > 0 ? (
                    <Animated.View exiting={FadeOut.duration(180)} layout={LinearTransition.duration(250)}>
                      <ExpiredLoans visibleExpiredLoans={visibleExpiredLoans} onHideLoan={hideExpiredLoan} />
                    </Animated.View>
                  ) : null}
                  {!shouldHideBonificationBanner ? (
                    <Animated.View exiting={FadeOut.duration(180)} layout={LinearTransition.duration(250)}>
                      <BonificationPrompt bannerState={stableBonificationBanner} />
                    </Animated.View>
                  ) : null}
                </Column>
              ),
            },
          ]
        : []),
      {
        key: 'profile-summary',
        node: (
          <LandingProfileSummary
            addressLabel={addressLabel}
            greeting={greeting}
            navigation={navigation}
            profile={profile}
            user={user}
            variant="small"
          />
        ),
      },
      ...(isLoggedIn && !completeProfilePromptCompleted && !hideStepsToComplete
        ? [{ key: 'steps-to-complete', node: <ProfileCompletionSteps {...completeProfileSteps} /> }]
        : []),
      ...(freeSubAvailable && typeof freeSubNumDaysLeft === 'number'
        ? [
            {
              key: 'trial-banner',
              node: (
                <TrialBanner
                  freeSubAvailable={freeSubAvailable}
                  freeSubNumDaysLeft={freeSubNumDaysLeft}
                  onSubscribe={actions.openMyPlan}
                />
              ),
            },
          ]
        : []),
      ...(isLoggedIn
        ? [
            {
              key: 'quick-actions',
              node: (
                <LandingQuickActions
                  actions={actions}
                  activitySubtitle={activitySubtitle}
                  isLoggedIn={isLoggedIn}
                  statsLoaded={Boolean(stats && unreadData)}
                  unreadCount={unreadCount}
                  variant="small"
                  hasLateLoans={visibleExpiredLoans.length > 0}
                />
              ),
            },
          ]
        : []),
      ...(showTopMultipleOfferCard
        ? [{ key: 'multiple-offer-card-top', node: <MultipleOfferCard onPublish={dismissTopMultipleOfferCard} /> }]
        : []),
      ...(isLoggedIn && canViewActivitySummary && (!loansQuerySettled || hasUpcomingActivity)
        ? [
            {
              key: 'upcoming-activity',
              node: (
                <UpcomingActivity
                  borrowings={borrowings ?? []}
                  excludedLoanIds={visibleExpiredLoans.map((loan) => loan.id)}
                  isLoading={!loansQuerySettled}
                  lendings={lendings ?? []}
                  onViewAll={actions.openManagementOverview}
                  variant="small"
                />
              ),
            },
          ]
        : []),
      ...(showRecommendationSection(urgentNeedsRecommendations, { hasEmptyState: true })
        ? [
            {
              key: 'urgent-needs',
              node: (
                <ListingListWithStrategy
                  strategyProps={{ strategy: Strategies.UrgentNeeds }}
                  recommendationState={urgentNeedsRecommendations}
                  recommendations={urgentNeedsRecommendations.recommendations}
                  onViewAll={actions.openRequests}
                  card={ListingRequestCard}
                  cardProps={{ showUser: true }}
                  emptyState={<RequestEmptyState onPressViewAll={actions.openRequests} />}
                  titleSuffix={<CountBadge count={urgentNeedsCount} />}
                />
              ),
            },
          ]
        : []),
      ...(hasPartagePlusSubscription === false
        ? [
            {
              key: 'unlock-full-experience',
              node: <UnlockFullExperienceCard onPress={actions.openPayment} variant="small" />,
            },
          ]
        : []),
      ...(showRecommendationSection(recentOffersRecommendations)
        ? [
            {
              key: 'recent-offers',
              node: (
                <ListingListWithStrategy
                  strategyProps={{ strategy: Strategies.RecentOffers }}
                  recommendationState={recentOffersRecommendations}
                  recommendations={recentOffersRecommendations.recommendations}
                  onViewAll={actions.openRecentOffers}
                  card={ListingOfferCardSmall}
                />
              ),
            },
          ]
        : []),
      ...(showLandingCategories
        ? [
            {
              key: 'popular-categories',
              node: (
                <PopularCategories onCategoryPress={actions.openOfferCategory} onViewAll={actions.openExploreOffers} />
              ),
            },
          ]
        : []),
      ...(showLandingCategories
        ? [
            {
              key: 'category-offers',
              node: (
                <LandingCategoryOffers
                  query={categoryOffers}
                  onCategoryPress={actions.openOfferCategory}
                  variant="small"
                />
              ),
            },
          ]
        : []),
      ...(isWhiteLabel(WhiteLabel.HauteGaspesie)
        ? [
            {
              key: 'categories',
              node: (
                <LandingCategories
                  onTagPress={actions.openOfferTag}
                  onViewAll={actions.openExploreOffers}
                  variant="small"
                />
              ),
            },
          ]
        : []),
      ...(showRecommendationSection(recentLoansRecommendations, { hasEmptyState: showInviteOthersPrompt })
        ? [
            {
              key: 'recent-loans',
              node: (
                <ListingListWithStrategy
                  strategyProps={{ strategy: Strategies.RecentLoans }}
                  recommendationState={recentLoansRecommendations}
                  recommendations={recentLoansRecommendations.recommendations}
                  renderItem={(listing) =>
                    isWhiteLabel(WhiteLabel.HauteGaspesie) ? (
                      <RecentExchangeCard listing={listing as RecentLoanListing} />
                    ) : (
                      <RecentLoanCard listing={listing as RecentLoanListing} />
                    )
                  }
                  titleSuffix={
                    <Text style={{ marginRight: 'auto', marginLeft: theme.spacing['spacing.general.x1'] }}>
                      {isWhiteLabel(WhiteLabel.HauteGaspesie) ? '✨' : '🤝'}
                    </Text>
                  }
                  emptyState={showInviteOthersPrompt ? <InviteOthersPrompt useImage compactImage={false} /> : null}
                />
              ),
            },
          ]
        : []),
    ];

    if (isWhiteLabel(WhiteLabel.Refrain)) {
      const recentOffersIndex = items.findIndex((section) => section.key === 'recent-offers');
      const recentLoansIndex = items.findIndex((section) => section.key === 'recent-loans');
      if (recentOffersIndex !== -1 && recentLoansIndex !== -1) {
        const recentLoansSections = items.splice(recentLoansIndex, 1);
        items.splice(recentOffersIndex + 1, 0, ...recentLoansSections);
      }
    }

    if (isNeighbourhoodStatusPending) {
      return items;
    }

    if (immatureNeighbourhood && !isWhiteLabel(WhiteLabel.HauteGaspesie) && !showDiscoveryFeeds) {
      if (immatureNeighbourhood.defricheurEligible) {
        items.push({ key: 'immature-defricheur', node: <DefricheurCard /> });
      }
      if (immatureNeighbourhood.referral) {
        items.push({
          key: 'immature-referral',
          node: <ReferralInviteCard referral={immatureNeighbourhood.referral} />,
        });
      }
      if (!showTopMultipleOfferCard) items.push({ key: 'multiple-offer-card-bottom', node: <MultipleOfferCard /> });
      items.push(
        ...(canBecomeAmbassador ? [{ key: 'ambassador-users-banner', node: <AmbassadorUsersBanner /> }] : []),
        ...(user?.insured && !hideInsuredUser
          ? [{ key: 'insured-user', node: <InsuredUser placement="landing-page" otherUser={user} /> }]
          : []),
      );
      if (immatureNeighbourhood.writeToMayor && !isAnyWhiteLabel()) {
        items.push({ key: 'immature-mayor', node: <WriteToMayorCard /> });
      }
      if (immatureNeighbourhood.joinSocialMedia) {
        items.push({ key: 'immature-social', node: <SocialMediaCard /> });
      }
      return items;
    }

    items.push(
      ...[
        ...(showRecommendationSection(mixedRecommendations)
          ? [
              {
                key: 'mixed-recommendations',
                node: (
                  <ListingListWithStrategy
                    strategyProps={{
                      strategy: Strategies.UseMixedRecommendations,
                      recommendationType: ListingType.offer,
                      similarityType: FeatureType.CONTENT,
                    }}
                    recommendationState={mixedRecommendations}
                    recommendations={mixedRecommendations.recommendations}
                    card={ListingOfferCardSmall}
                    titleSuffix={
                      <Text style={{ marginRight: 'auto', marginLeft: theme.spacing['spacing.general.x1'] }}>✨</Text>
                    }
                  />
                ),
              },
            ]
          : []),
        ...(canBecomeAmbassador ? [{ key: 'ambassador-users-banner', node: <AmbassadorUsersBanner /> }] : []),
        ...(showRecommendationSection(nearestSharingPlaceRecommendations, { ignoresFeatureFlag: true })
          ? [
              {
                key: 'nearest-sharing-place',
                node: (
                  <ListingListWithStrategy
                    strategyProps={{ strategy: Strategies.NearestSharingPlace }}
                    recommendationState={nearestSharingPlaceRecommendations}
                    recommendations={nearestSharingPlaceRecommendations.recommendations}
                    renderItem={(place) => <SharingPlaceCard place={place as SharingPlace} />}
                    titleSuffix={
                      <Text style={{ marginRight: 'auto', marginLeft: theme.spacing['spacing.general.x1'] }}>🏠</Text>
                    }
                    emptyState={null}
                  />
                ),
              },
            ]
          : []),
        ...(showRecommendationSection(matchingRequestsRecommendations)
          ? [
              {
                key: 'matching-requests',
                node: (
                  <ListingListWithStrategy
                    strategyProps={{
                      strategy: Strategies.UseMatchNeedsWithOffers,
                      recommendationType: ListingType.request,
                      similarityType: FeatureType.CONTENT,
                    }}
                    recommendationState={matchingRequestsRecommendations}
                    recommendations={matchingRequestsRecommendations.recommendations}
                    onViewAll={() => navigation.navigate('all-listings', { type: ListingType.request }, { pop: true })}
                    card={ListingRequestCard}
                    cardProps={{ showUser: true }}
                  />
                ),
              },
            ]
          : []),
        ...(visibleRandomInterestRecommendations?.length
          ? [
              {
                key: 'your-interest-random',
                node: (
                  <LandingInterestRecommendationsBlock
                    onOpenOfferTag={actions.openOfferTag}
                    recommendations={visibleRandomInterestRecommendations}
                    tags={tags}
                  />
                ),
              },
            ]
          : []),
        ...(user?.insured && !hideInsuredUser
          ? [{ key: 'insured-user', node: <InsuredUser placement="landing-page" otherUser={user} /> }]
          : []),
        ...(showRecommendationSection(mostPopularRecommendations)
          ? [
              {
                key: 'most-popular',
                node: (
                  <ListingListWithStrategy
                    strategyProps={{
                      strategy: Strategies.MostPopular,
                      recommendationType: ListingType.offer,
                      similarityType: FeatureType.CONTENT,
                    }}
                    recommendationState={mostPopularRecommendations}
                    recommendations={mostPopularRecommendations.recommendations}
                    backgroundColor={theme.colors['surface.inverse.tertiary-neutral']}
                    titleSuffix={
                      <Text style={{ marginRight: 'auto', marginLeft: theme.spacing['spacing.general.x1'] }}>🏆</Text>
                    }
                  />
                ),
              },
            ]
          : []),
        ...(!showTopMultipleOfferCard ? [{ key: 'multiple-offer-card-bottom', node: <MultipleOfferCard /> }] : []),
        ...(showRecommendationSection(favouritesRecommendations, { hasEmptyState: true })
          ? [
              {
                key: 'favourites',
                node: (
                  <ListingListWithStrategy
                    strategyProps={{ strategy: Strategies.YourFavourites }}
                    recommendationState={favouritesRecommendations}
                    recommendations={favouritesRecommendations.recommendations}
                    titleSuffix={
                      <Text style={{ marginRight: 'auto', marginLeft: theme.spacing['spacing.general.x1'] }}>❤️</Text>
                    }
                    onViewAll={actions.openFavourites}
                    emptyState={
                      <FavouritesEmptyState
                        buttonText={t('your-favourites.emptyStateButton')}
                        onExplore={actions.openExploreOffers}
                        title={t('your-favourites.emptyStateTitle')}
                        subtitle={t('your-favourites.emptyStateText')}
                      />
                    }
                  />
                ),
              },
            ]
          : []),
        ...(showPopularInterests
          ? [
              {
                key: 'popular-interests',
                node: <PopularInterests onTagPress={actions.openOfferTag} tags={popularInterestsQuery.data} />,
              },
            ]
          : []),
        ...(visibleGeneralInterestRecommendations?.length
          ? [
              {
                key: 'your-interest-general',
                node: (
                  <LandingInterestRecommendationsBlock
                    onOpenOfferTag={actions.openOfferTag}
                    recommendations={visibleGeneralInterestRecommendations}
                    tags={tags}
                  />
                ),
              },
            ]
          : []),
      ],
    );

    if (neighbourhoodStatus?.joinSocialMedia) {
      items.push({ key: 'immature-social', node: <SocialMediaCard /> });
    }

    if (neighbourhoodStatus?.writeToMayor && !isAnyWhiteLabel()) {
      items.push({ key: 'immature-mayor', node: <WriteToMayorCard /> });
    }

    if (canPublish) {
      items.push(
        {
          key: 'post-need-prompt',
          node: (
            <PostNeedPromptCard
              buttonText="postNeedPrompt.afterResults.button"
              newListingSource="babillard_after_search_results"
              text="postNeedPrompt.afterResults.text"
              title="postNeedPrompt.afterResults.title"
              hideStats
              v2
              style={{ marginTop: theme.spacing['spacing.general.x2'] }}
            />
          ),
        },
        {
          key: 'post-offer-prompt',
          node: (
            <PostOfferPromptCard
              buttonText="postOfferPrompt.first.button"
              newListingSource="babillard_after_search_results"
              text="postOfferPrompt.first.text"
              title="postOfferPrompt.first.title"
              hideStats
              v2
              style={{ marginTop: theme.spacing['spacing.general.x2'] }}
            />
          ),
        },
      );
    }

    return items;
  })();

  return {
    activeTab,
    loadMoreCategoryOffers: categoryOffers.loadMore,
    handleLandingTabPress,
    handleSearchSubmit,
    handleTagPress,
    handleCategoryPress,
    handleViewAllListings,
    isHomeExploreTutorialActive,
    isSearchOverlayVisible,
    keyExtractor,
    renderSection,
    sections,
    setIsSearchOverlayVisible,
    tabs,
  };
};

function LandingPageScreenView({
  activeTab,
  loadMoreCategoryOffers,
  handleLandingTabPress,
  handleSearchSubmit,
  handleTagPress,
  handleCategoryPress,
  handleViewAllListings,
  isHomeExploreTutorialActive,
  isSearchOverlayVisible,
  keyExtractor,
  renderSection,
  sections,
  setIsSearchOverlayVisible,
  tabs,
}: ReturnType<typeof useLandingPageScreenModel>) {
  const insets = useSafeAreaInsets();
  const isFocused = useIsFocused();
  // Scroll chrome state must not rerun the landing data model and all of its
  // recommendation queries when the user changes scroll direction.
  const { handleScroll, headerSearchBarRef, isSearchBarVisible, listRef, searchBarAnimatedStyle } = useLandingSearchBar(
    { activeTab, isFocused, isHomeExploreTutorialActive },
  );
  // Native navigation overlays this screen so hiding it never resizes the list.
  const bottomPadding = isMobileNative
    ? 60 + (insets.bottom || theme.spacing['spacing.general.x4']) + HOME_LIST_CONTENT_CONTAINER_STYLE.paddingBottom
    : HOME_LIST_CONTENT_CONTAINER_STYLE.paddingBottom;
  return (
    <Column flex={1} testID="landing-page-screen">
      {isSearchOverlayVisible ? (
        <SearchOverlay
          onClose={() => setIsSearchOverlayVisible(false)}
          onSubmit={handleSearchSubmit}
          onTagPress={handleTagPress}
          onCategoryPress={handleCategoryPress}
          onViewAllListings={handleViewAllListings}
        />
      ) : null}

      <LandingFeedPager
        tabs={tabs.landingTabs}
        value={activeTab}
        onChange={handleLandingTabPress}
        showTabs={!isSearchOverlayVisible}
        swipeEnabled={!isSearchOverlayVisible && !isHomeExploreTutorialActive}
        scenes={[
          <View key="home" style={{ flex: 1 }}>
            {!isSearchOverlayVisible ? (
              <Animated.View
                ref={headerSearchBarRef}
                collapsable={false}
                pointerEvents={isSearchBarVisible ? 'auto' : 'none'}
                style={[
                  searchBarAnimatedStyle,
                  {
                    overflow: 'hidden',
                    position: 'absolute',
                    top: 0,
                    zIndex: 999,
                    width: '100%',
                    backgroundColor: 'white',
                    justifyContent: 'center',
                  },
                ]}
              >
                <View
                  style={{
                    paddingHorizontal: theme.spacing['spacing.general.x4'],
                  }}
                >
                  <Pressable testID="landing-search-button" onPress={() => setIsSearchOverlayVisible(true)}>
                    <SearchInput initialValue="" onSubmit={() => undefined} editable={false} pointerEvents="none" />
                  </Pressable>
                </View>
              </Animated.View>
            ) : null}

            <FlatList
              ref={listRef}
              style={{ flex: 1, backgroundColor: theme.colors['surface.default'] }}
              contentContainerStyle={[HOME_LIST_CONTENT_CONTAINER_STYLE, { paddingBottom: bottomPadding }]}
              data={sections}
              initialNumToRender={4}
              maxToRenderPerBatch={1}
              windowSize={21}
              ListHeaderComponent={<Column height={SEARCH_BAR_CONTAINER_HEIGHT} />}
              ItemSeparatorComponent={LandingPageSectionSeparator}
              keyExtractor={keyExtractor}
              onEndReached={activeTab === 'home' ? loadMoreCategoryOffers : undefined}
              onEndReachedThreshold={0.5}
              onScroll={activeTab === 'home' ? handleScroll : undefined}
              renderItem={renderSection}
              scrollEnabled={!isHomeExploreTutorialActive}
              scrollEventThrottle={16}
              showsVerticalScrollIndicator={false}
            />
          </View>,
          <View key="feed" style={{ flex: 1 }}>
            <PostsList
              isActive={activeTab === 'feed'}
              isEndOfFeed={tabs.isEndOfFeed}
              isFetchingNextPage={tabs.isFetchingNextPage}
              isLoading={tabs.arePostsPending}
              onEndReached={activeTab === 'feed' ? tabs.loadMorePosts : undefined}
              onScroll={activeTab === 'feed' ? handleScroll : undefined}
              posts={tabs.recentPosts}
              showListingAvatar
              contentContainerStyle={{
                paddingBottom: bottomPadding,
                paddingTop: 0,
              }}
            />
          </View>,
        ]}
      />

      <SubscriptionCodeModalPrompt />
    </Column>
  );
}

export const LandingPageScreenContent: NavigationScreen<'landing-page'> = (props) => (
  <LandingPageScreenView {...useLandingPageScreenModel(props)} />
);
